/* Related Finished carousel — Paolina Roma (Dawn)
   Custom element behind sections/pr-product-related-finished.liquid.
   One instance per group, two groups can live in the same section, so every
   lookup is scoped to the element's own group ([data-group]) and never global.

   What it does:
   - writes data-start / data-end on itself, which drive the CSS fade edges
   - enables/disables the prev and next arrows at the ends of the track
   - shows the arrow pair (data-show) only when the track can actually scroll
   - scrolls by exactly one card plus one gap on click

   Self-registering, so it survives shopify:section:load in the theme editor,
   and it tears its observers and listeners down on disconnect. No dependency
   on global.js, pubsub.js or constants.js. */

if (!customElements.get('pr-related-carousel')) {
  customElements.define(
    'pr-related-carousel',
    class PrRelatedCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;

        /* The arrows sit in the group header, a sibling of this element, so the
           scope is the group wrapper. Two carousels in one section still never
           see each other's controls. */
        const scope = this.closest('[data-group]') || this;
        this.controls = scope.querySelector('[data-controls]');
        this.prev = scope.querySelector('[data-prev]');
        this.next = scope.querySelector('[data-next]');

        /* Programmatic focus target only, it must not enter the tab order.
           Used by updateState() when an arrow disables itself while focused
           and the other arrow is not a valid landing spot. */
        if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');

        this.onScroll = this.onScroll.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onResize = this.onResize.bind(this);
        this.ticking = false;

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        if (this.prev) this.prev.addEventListener('click', this.onPrev);
        if (this.next) this.next.addEventListener('click', this.onNext);

        if (typeof ResizeObserver !== 'undefined') {
          this.observer = new ResizeObserver(this.onResize);
          this.observer.observe(this.track);
        } else {
          window.addEventListener('resize', this.onResize, { passive: true });
        }

        this.toggleControls();
        this.updateState();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.prev) this.prev.removeEventListener('click', this.onPrev);
        if (this.next) this.next.removeEventListener('click', this.onNext);
        window.removeEventListener('resize', this.onResize);

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.updateState();
        });
      }

      onResize() {
        this.toggleControls();
        this.updateState();
      }

      /* Read at scroll time, not once in connectedCallback: the system
         preference can change while the session is open. Same pattern as
         assets/section-pr-product-variant-showcase.js. An explicit
         behavior: 'smooth' beats the CSS scroll-behavior, so the
         @media (prefers-reduced-motion: reduce) block in the stylesheet
         cannot neutralise the arrows on its own. */
      prefersReduce() {
        return (
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      }

      onPrev() {
        this.track.scrollBy({ left: -this.step(), behavior: this.prefersReduce() ? 'auto' : 'smooth' });
      }

      onNext() {
        this.track.scrollBy({ left: this.step(), behavior: this.prefersReduce() ? 'auto' : 'smooth' });
      }

      canScroll() {
        return this.track.scrollWidth - this.track.clientWidth > 4;
      }

      toggleControls() {
        if (!this.controls) return;
        this.controls.toggleAttribute('data-show', this.canScroll());
      }

      updateState() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const pos = this.track.scrollLeft;
        const isStart = pos <= 2;
        const isEnd = pos >= max - 2;

        this.setAttribute('data-start', isStart ? 'true' : 'false');
        this.setAttribute('data-end', isEnd ? 'true' : 'false');

        /* Disabling the button that currently holds the focus drops the focus
           on <body>, so a keyboard user who reaches the end of the row with
           repeated Enter presses loses the reading position and Tab restarts
           from the top of the document. Move the focus first: to the opposite
           arrow when it stays enabled and visible, otherwise to the host
           element (both arrows disabled and [data-controls] hidden happens on
           a resize that makes the track unscrollable). */
        const active = document.activeElement;
        const rescue = (btn, willDisable) => {
          if (!btn || !willDisable || active !== btn) return;
          const other = btn === this.next ? this.prev : this.next;
          if (other && !other.disabled && other.offsetParent !== null) other.focus();
          else this.focus();
        };
        rescue(this.prev, isStart);
        rescue(this.next, isEnd);

        if (this.prev) this.prev.toggleAttribute('disabled', isStart);
        if (this.next) this.next.toggleAttribute('disabled', isEnd);
      }

      /* One card plus one gap. Falls back to most of the viewport width when
         the track is empty, which only happens if every reference was dropped. */
      step() {
        const card = this.track.querySelector('[data-card]');
        if (!card) return this.track.clientWidth * 0.8;

        const styles = getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 16;
        return card.getBoundingClientRect().width + gap;
      }
    }
  );
}
